import React, { useEffect, useMemo, useRef, useState } from 'react';
import { LandingPage } from '@bmsp/bmsp.webcomponent.organization.management';
import './OrganisationManagementNew.scss';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  IOrganisationState,
  setOrganizationModalValue
} from '../../commonUtils/Redux/Global/globalShared';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { authService } from '../../../auth/services';
import { AuthenticatedUser } from '../../../auth/models';
import { useAuthService } from '../../../auth/hooks/useAuthService';
import {
  getFeatureFlagValuebasedOnSIOrg,
  getFeatureFlagValuebasedOnSIOrgWhitelist,
  isFeatureFlagEnabledForUser
} from '../../commonUtils/LDFlagUtils';
import {
  Redirect,
  useLocation,
  useHistory,
  useRouteMatch
} from 'react-router-dom';
import i18next from '../../../i18next';
import { ILandingPage } from '@bmsp/bmsp.webcomponent.organization.management/lib/src/components/LandingPage';
import { ActivityTable } from '../../activity';
import { User } from 'oidc-client';
interface IPermission {
  name: string;
}

interface IOrganisationManagementNewProps {
  loggedInUser: AuthenticatedUser | null;
}

export const OrganisationManagementNew: React.FC<IOrganisationManagementNewProps> = ({
  loggedInUser
}) => {
  const [
    licenseManagementPrivileges,
    setLicenseManagementPrivileges
  ] = useState<IPermission[]>([]);

  // Get globalPermissions and fetchUserAllPrivileges from hook
  const {
    user,
    globalPermissions,
    loadGlobalPermissions,
    fetchUserAllPrivileges
  } = useAuthService();

  const [
    activityLogPermissions,
    setActivityLogPermissions
  ] = useState<IPermission[]>([]);

  // Fetch LicenseManagement user-all-privileges
  useEffect(() => {
    if (user && globalPermissions.length === 0) {
      loadGlobalPermissions().catch((error) => {
        console.error('OrganisationManagementNew Failed to load global permissions:', error);
      });
    }
  }, [user, globalPermissions.length, loadGlobalPermissions]);

  // Fetch LicenseManagement user-all-privileges using hook method
  useEffect(() => {
    fetchUserAllPrivileges('LicenseManagement').then(setLicenseManagementPrivileges);
  }, [fetchUserAllPrivileges]);

  const orgSelected = useSelector(
    (state: IOrganisationState) =>
      state.globalShared.gatewaySelectedOrganisation
  );
  const userProfile = useSelector(
    (state: IOrganisationState) => state.globalShared.userList
  );
  const organisations = useSelector(
    (state: IOrganisationState) => state.globalShared.organisationList
  );
  const featureFlags = useFlags();

  // Fetch ActivityLog user-all-privileges when the feature flag is enabled
  useEffect(() => {
    if (featureFlags['enableActivityLog']) {
      authService.fetchUserAllPrivileges('ActivityLog').then(setActivityLogPermissions);
    }
  }, [featureFlags['enableActivityLog']]);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const navParams = useParams<{
    rootId: string;
    custId: string;
    siteId: string;
  }>();

  const navigationParams = {
    contractorId: params.get('contractorId'),
    orgName: params.get('orgName'),
    customerId: params.get('customerId'),
    orgId: params.get('orgId'),
    siteId: params.get('siteId'),
    siteName: params.get('siteName')
  };
  
  // Check if user has at least one of the required activity log privileges (OR logic)
  const activityLogPrivileges = ['ActivityLog_Write'];
  const hasActivityLogPermission = activityLogPrivileges.some(privilege =>
    activityLogPermissions.some(permission => permission.name === privilege)
  );

  const featureFlagsProps = {
    ...featureFlags,
    enableBacsFlowDistributor:
      featureFlags['enableBacsFlowDistributor'] ?? false,
    enableBacsuiv2: featureFlags['enableBacsuiv2'] ?? false,
    enableBacsFeatureSiteType:
      featureFlags['enableBacsFeatureSiteType'] ?? false,
    enableRename: featureFlags?.['enableRename'] ?? false,
    enableARRLicenseManagement: getFeatureFlagValuebasedOnSIOrgWhitelist(
      'enableArrLicenseFlowForSi',
      featureFlags,
      orgSelected,
      organisations
    ),
    enableActivityLog: !!featureFlags['enableActivityLog'] && hasActivityLogPermission,
    enableARRLicenseOverviewAndDownloadForSI: authService.isLicensePrivilegedUser
  };

  const dispatch = useDispatch();

  const organizationValue = useSelector(
    (state: IOrganisationState) => state.globalShared.organizationModalValue
  );

  const handleOrganisationModal = () => {
    dispatch(setOrganizationModalValue(!organizationValue));
  };
  const history = useHistory();

  const gwRedirectTrigger = () => {
    history.push('/app/feature/gateway-management');
  };

  const projectOsRedirectTrigger = () => {
    history.push('/app/feature/project-management');
  };

  const subscriptionRedirectTrigger = () => {
    history.push('/app/feature/subscription');
  };

  const userManagementRedirectTrigger = () => {
    history.push('/app/feature/users');
  };

  const siteRouteMatch = useRouteMatch<{
    rootId: string;
    custId: string;
    siteId: string;
  }>({ path: '/app/feature/organisations/:rootId/customers/:custId/sites/:siteId', exact: true });

  // Switching the global org while on a site route returns to that org's customer page, as on master.
  const prevOrgIdRef = useRef(orgSelected?.id);
  useEffect(() => {
    const prevOrgId = prevOrgIdRef.current;
    prevOrgIdRef.current = orgSelected?.id;
    if (siteRouteMatch && prevOrgId && orgSelected?.id && prevOrgId !== orgSelected.id) {
      history.replace('/app/feature/organisations');
    }
  }, [orgSelected?.id]);

  const onNavigateToSite = ({ custId, siteId, tabHash }: { custId: string; siteId: string; tabHash?: string }) => {
    const rootId = orgSelected?.id ?? siteRouteMatch?.params.rootId;
    if (!rootId) {
      return;
    }
    const seg = (v: string) => encodeURIComponent(v);
    history.push(
      `/app/feature/organisations/${seg(rootId)}/customers/${seg(custId)}/sites/${seg(siteId)}${tabHash ? `#${tabHash}` : ''}`,
      { fromSiteList: true }
    );
  };

  const onNavigateToSiteList = () => {
    // Opened from the site list: the customer view is still mounted, so just pop history.
    if ((location.state as { fromSiteList?: boolean } | undefined)?.fromSiteList) {
      history.goBack();
      return;
    }
    // Opened from an external URL: go to the host main route, as on master.
    history.replace('/app/feature/organisations');
  };

  const onTabHashChange = (tabHash: string) => {
    history.replace({ ...location, hash: tabHash ? `#${tabHash}` : '' });
  };

  const siteRoute = siteRouteMatch
    ? {
        ...siteRouteMatch.params,
        hash: location.hash.replace(/^#/, '')
      }
    : undefined;

  // Combine hook-based globalPermissions with licenseManagementPrivileges
  const combinedPermissions = [
    ...globalPermissions,
    ...licenseManagementPrivileges
  ];

  const isBmsAssistProjectOsSubscriptionEnabled = isFeatureFlagEnabledForUser(
    'bmsAssistProjectOsSubscription',
    featureFlags,
    userProfile?.email
  );
  const isBmsAssistProjectOsSubscriptionEnabledByOrgId = isFeatureFlagEnabledForUser(
    'bmsAssistProjectOsSubscription',
    featureFlags,
    orgSelected?.id
  );

  const organisationManagementProps = ({
    userProfile,
    featureFlags: featureFlagsProps,
    selectedSIOrg: orgSelected ?? '',
    globalPermissions: combinedPermissions,
    handleOrganisationModal,
    gwRedirectTrigger,
    projectOsRedirectTrigger,
    subscriptionRedirectTrigger,
    userManagementRedirectTrigger,
    siteRoute,
    onNavigateToSite,
    onNavigateToSiteList,
    onTabHashChange,
    navigationParams,
    i18Lng: i18next,
    isBmsAssistProjectOsSubscriptionEnabled,
    isBmsAssistProjectOsSubscriptionEnabledByOrgId,
    cloudProvider: (window as any).ENV.CLOUD_PROVIDER
  } as unknown) as ILandingPage & {
    i18Lng: any;
    isBmsAssistProjectOsSubscriptionEnabled: boolean;
    isBmsAssistProjectOsSubscriptionEnabledByOrgId: boolean;
  };

  useEffect(() => {
    const handleNavigation = (event: CustomEvent) => {
      const { path, params } = event.detail;
      const fullPath = `${path}?${new URLSearchParams(params)}`;
      history.push(fullPath);
    };

    window.addEventListener(
      'bmsp:organization-customer-tab',
      handleNavigation as EventListener
    );
    return () => {
      window.removeEventListener(
        'bmsp:organization-customer-tab',
        handleNavigation as EventListener
      );
    };
  }, [history]);

  const isSiteNavigationEnabled = useMemo((): boolean => {
    const custId = navParams.custId;
    if (!custId) return false;

    const rawFlags = Array.isArray(featureFlags?.['enableSiteNavigation'])
      ? featureFlags['enableSiteNavigation']
      : [];
    const flagsSet = new Set<string>(rawFlags);

    return flagsSet.has("exclude")
      ? !flagsSet.has(custId)
      : flagsSet.has("*") || flagsSet.has(custId);
      
  }, [featureFlags?.['enableSiteNavigation'], navParams.custId]);

  // Flag off: a site URL behaves as on master and falls back to the organisations page.
  if (siteRouteMatch && !(featureFlags['enableBacsuiv2'] && isSiteNavigationEnabled)) {
    return <Redirect to="/app/feature/organisations" />;
  }

  return (
    <>
      <div className="new-organization-container">
        <LandingPage {...organisationManagementProps} activityContent={<ActivityTable />}/>
      </div>
    </>
  );
};

export default OrganisationManagementNew;
